/* eslint-disable react/destructuring-assignment */
import React from 'react'
import { Button, Table, Typography } from 'antd'
import moment from 'moment'
import { ClientProfile, Plan } from 'types'
import PlanHistoryTable from 'component/Client/PlanHistoryTable'
import copyToClipboard from 'utils/copyToClipboard'
import { CopyOutlined } from '@ant-design/icons'
import addCurrencySymbol from 'utils/addCurrencySymbol'
import { useAppSelector } from 'hooks/reduxHooks'
import getPlatformLabel from 'utils/getPlatformLabel'
import toTileCase from 'utils/toTitleCase'

const { Title } = Typography

/**
 * Overview Columns for Table of Single client
 */

const overviewColumns = [
	{ title: 'Fields', dataIndex: 'title', key: 'title' },
	{
		title: 'Details',
		dataIndex: 'dataIndex',
		key: 'dataIndex',
	},
]

/**
 * ClientsTable component props
 */

interface PropTypes {
	overView: ClientProfile | null
	planHis: Plan[] | []
	loading: boolean
}

const OverViewTable: React.FC<PropTypes> = ({ overView, planHis, loading }) => {
	/**
	 * Overview Data for Single client
	 */

	const currency =
		useAppSelector(
			state => state.singleClient.clientProfile?.currency_selected
		) || 'INR'

	const formatChargeField = (value?: number | null) =>
		value === null || value === undefined || value === -1
			? 'Default (pricing chart)'
			: addCurrencySymbol(value, currency)

	const overviewData = [
		{
			title: 'Name',
			dataIndex: overView?.organizationName,
		},
		{
			title: 'Logo',
			dataIndex: overView?.logo ? (
				<img
					src={overView.logo}
					alt={overView?.organizationName}
					style={{ width: 50, height: 50 }}
				/>
			) : (
				'--'
			),
		},
		{
			title: 'Mobile',
			dataIndex: `${overView?.mobileNumber?.code} ${overView?.mobileNumber?.mobile}`,
		},
		{
			title: 'Email',
			dataIndex: overView?.email,
		},
		{
			title: 'Notification Emails',
			dataIndex: overView?.notificationEmails,
		},
		{
			title: 'Billing Emails',
			dataIndex: overView?.invoiceEmails,
		},
		{
			title: 'Website',
			dataIndex: overView?.website,
		},
		{
			title: 'Address',
			dataIndex: overView?.address,
		},
		{
			title: 'Country',
			dataIndex: overView?.country,
		},
		{
			title: 'State',
			dataIndex: overView?.state,
		},
		{
			title: 'City',
			dataIndex: overView?.city,
		},
		{
			title: 'Platform',
			dataIndex: overView?.selectedEDGEPlatform
				? getPlatformLabel(overView.selectedEDGEPlatform)
				: '-',
		},
		{
			title: 'Active Plan',
			dataIndex: overView?.activePlan?.billingCycle,
		},
		{
			title: 'Active On',
			dataIndex: overView?.activePlan
				? moment(overView?.activePlan?.createdAt).format('DD-MM-YYYY')
				: '--',
		},
		{
			title: 'Active',
			dataIndex: overView?.active ? 'Yes' : 'No',
		},
		{
			title: 'Free Trial',
			dataIndex: overView?.isFreeTrial ? 'Yes' : 'No',
		},
		{
			title: 'Platform Mode',
			dataIndex: toTileCase(overView?.platformMode ?? 'LIVE'),
		},
		{
			title: 'Webhook URL',
			dataIndex: (
				<Button
					style={{
						cursor: 'copy',
					}}
					type='text'
					htmlType='button'
					onClick={() =>
						copyToClipboard(
							overView?.webhookUrl || '',
							'Webhook URL copied to clipboard'
						)
					}
					icon={<CopyOutlined />}
				>
					{overView?.webhookUrl || '--'}
				</Button>
			),
		},
		{
			title: 'Webhook Token',
			dataIndex: (
				<Button
					style={{
						cursor: 'copy',
					}}
					type='text'
					htmlType='button'
					onClick={() =>
						copyToClipboard(
							overView?.webhookToken || '',
							'Webhook Token copied to clipboard'
						)
					}
					icon={<CopyOutlined />}
				>
					{overView?.webhookToken || '--'}
				</Button>
			),
		},
		{
			title: 'Created At',
			dataIndex: overView ? moment(overView.createdAt).format('DD-MM-YYYY') : '--',
		},
		{
			title: 'Vendor',
			dataIndex: overView?.vendorSelected,
		},
		{
			title: 'Will deduct TDS',
			dataIndex: overView?.tdsEnabled ?? false ? 'Yes' : 'No',
		},
		{
			title: 'Should Auto Generate Wallet Invoice',
			dataIndex:
				overView?.shouldGenerateAutoInvoice ?? true ? 'Enabled' : 'Disabled',
		},
		{
			title: 'Currency',
			dataIndex: overView?.currency_selected,
		},
		{
			title: 'WhatCX Charge',
			dataIndex: formatChargeField(overView?.whatcxCharge),
		},
		{
			title: 'Inbound Conversation Charge',
			dataIndex: formatChargeField(overView?.whatcxChargeInbound),
		},
		{
			title: 'Outbound Conversation Charge',
			dataIndex: formatChargeField(overView?.whatcxChargeOutbound),
		},
		{
			title: 'Invoice Details',
			dataIndex: (
				<Table
					columns={[
						{ title: 'Label', dataIndex: 'key', key: 'key' },
						{
							title: 'Value',
							dataIndex: 'value',
							key: 'value',
						},
					]}
					dataSource={overView?.invoiceDetails || []}
					pagination={false}
				/>
			),
		},
		{
			title: 'Taxation',
			dataIndex: (
				<Table
					columns={[
						{ title: 'Tax', dataIndex: 'key', key: 'key' },
						{
							title: 'Percentage',
							dataIndex: 'percentage',
							key: 'percentage',
							render: p => `${p}%`,
						},
					]}
					dataSource={overView?.taxation || []}
					pagination={false}
				/>
			),
		},
		{
			title: 'Customer Success Managers',
			dataIndex: (
				<ul style={{ marginBottom: 0 }}>
					{overView?.relationShipManager
						?.map(admin =>
							typeof admin === 'string'
								? admin
								: `${admin.firstName} ${admin.lastName} (${admin.email} - ${admin.mobile})`
						)
						.map(user => <li>{user}</li>) || 'None Selected'}

					{overView?.relationShipManager?.length === 0 ? 'None Selected' : ''}
				</ul>
			),
		},
		{
			title: 'Notificaiton Emails',
			dataIndex: (
				<ul style={{ marginBottom: 0 }}>
					{overView?.notificationEmails?.split(',').map(email => <li>{email}</li>) ||
						'None Selected'}

					{overView?.notificationEmails?.split(',').length === 0
						? 'None Selected'
						: ''}
				</ul>
			),
		},
	]

	/**
	 * Wallet data for single client
	 */
	const walletData = [
		{
			active: overView?.wallet?.active ? 'Yes' : 'No',
			createdAt: overView?.wallet
				? moment(overView?.wallet.createdAt).format('DD-MM-YYYY')
				: null,
			credit: overView?.wallet?.amount?.credit,
			reserve: overView?.wallet?.amount?.reserve,
		},
	]

	/**
	 *  Wallet Columns for Table of Single client
	 */

	const walletColumns = [
		{
			title: 'Active',
			dataIndex: 'active',
		},
		{
			title: 'Created At',
			dataIndex: 'createdAt',
		},
		{
			title: 'Credit',
			dataIndex: 'credit',
			render: (credit: number) => addCurrencySymbol(credit, currency),
		},
		{
			title: 'Reserve',
			dataIndex: 'reserve',
		},
	]

	return (
		<>
			<div
				className='head-align-flex'
				style={{ paddingBottom: '1rem', marginTop: '1rem' }}
			>
				<div className='Title-Head'>
					<Title level={5}>Client Details</Title>
				</div>
			</div>
			<Table
				columns={overviewColumns}
				dataSource={overviewData}
				loading={loading}
				pagination={false}
			/>
			<div
				className='head-align-flex'
				style={{ paddingBottom: '1rem', marginTop: '2rem' }}
			>
				<div className='Title-Head'>
					<Title level={5}>Wallet Details</Title>
				</div>
			</div>
			<Table
				columns={walletColumns}
				dataSource={walletData}
				loading={loading}
				pagination={false}
			/>
			<div
				className='head-align-flex'
				style={{ paddingBottom: '1rem', marginTop: '2rem' }}
			>
				<div className='Title-Head'>
					<Title level={5}>Plan History</Title>
				</div>
			</div>
			<PlanHistoryTable planHis={planHis} loading={loading} />
		</>
	)
}

export default OverViewTable
